import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import FieldInput from './FieldInput';

import Tooltip from '../../misc/Tooltip';


const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

const inputGroupWrapStyles = (props) => `
  width: 100%;
  background-color: ${props.backgroundColor};
  font-size: ${props.fontSize}px !important;
  color: ${props.fontColor};
  font-style: ${props.fontStyle};
  font-weight: ${props.fontWeight};
  text-align: ${props.textAlign};
  text-transform: ${props.textTransform};
  text-decoration: ${props.textDecoration};
  border-radius: ${props.borderRadius}px;
  border: ${props.border};
  padding: ${props.padding}px;
  min-height: 25px;
  box-sizing: border-box;
`;

const StyledInputGroupDisplay = styled.div`
  ${(props) => inputGroupWrapStyles(props)}
  white-space: pre-line;
  overflow-wrap: break-word;
  cursor: pointer;

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

const StyledInputGroupTextarea = styled.textarea`
  ${(props) => inputGroupWrapStyles(props)}
  resize: none;
  overflow: hidden;
  transition: border 0.15s ease-in-out;

  &:focus, &:focus-visible {
    outline: unset;
    border: ${(props) => props.borderWidth}px solid ${(props) => props.primaryColor};
  }

  &:hover {
    ${(props) => (props.disabled ? '' : `border: ${props.borderWidth}px solid ${props.primaryColor}`)};
  }
`;

const StyledInternalWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const StyledTooltipWrapper = styled.span`
  position: absolute;
  right:0;
  top:0;
`;

const StyledFieldHeader = styled.span`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 6px;
  padding-bottom: 4px;
  padding-right: 20px;
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TextField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    type: PropTypes.string,
    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    wrapperStyles: PropTypes.object,
    inputGroupWrapMode: PropTypes.bool,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ])

  }

  static defaultProps = {
    value: '',
    wrapperStyles: {}
  }

  constructor(props) {
    super(props);
    const { value } = props;
    this.state = { focused: false, changes: value };
    this.textareaRef = createRef();
    this.wrapDisplayHeight = null;
  }

  moveCursorToEnd = () => {
    if (this.textareaRef.current) {
      const length = this.textareaRef.current.value.length;
      this.textareaRef.current.setSelectionRange(length, length);
    }
  };

  componentDidMount() {
    const { focused } = this.state;
    if (focused) this.moveCursorToEnd();
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { value } = this.props;
    if (value !== prevProps.value && value !== changes) this.setState({ changes: value });

  }

  componentWillUnmount() {

    const { value, disabled, onChange, fieldId, fieldIdentifier, fieldPositionId, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== value && !disabled) {
      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);
      if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

    }

  }

  render() {

    const { focused, changes } = this.state;

    const {

      wrapperStyles,

      fieldId,
      fieldIdentifier,
      fieldPositionId,

      type,
      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      tabIndex,

      primaryColor,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,

      //Field style
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onChange,
      onFocus,
      onBlur,
      onDecoratorClick,
      placeholder,
      fieldMode,
      pageLayoutMode,
      fieldPresentationMode,
      decorators,
      inputGroupWrapMode,

    } = this.props;

    const getTooltip = () => {

      if (disabled) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );

    };

    const border = `${borderWidth}px solid ${borderColor}`;
    const displayValue = getDisplayValue(type, changes);
    const isWrapMode = inputGroupWrapMode && textOverflow !== 'ellipsis';

    let fieldNode;

    if (isWrapMode) {

      const wrapTypography = {
        fontSize,
        fontColor,
        fontStyle,
        fontWeight,
        textAlign,
        textTransform,
        textDecoration,
        borderRadius,
        borderWidth,
        backgroundColor,
        padding,
        primaryColor,
        border,
      };

      if (focused && !disabled) {
        fieldNode = (
          <StyledInputGroupTextarea
            data-testid={`${fieldPositionId}-text-field`}
            autoFocus
            ref={(e) => {
              this.textareaRef.current = e;
              if (!e) return;
              e.focus();
            }}
            defaultValue={displayValue != null ? String(displayValue) : ''}
            style={this.wrapDisplayHeight ? { height: `${this.wrapDisplayHeight}px`, minHeight: `${this.wrapDisplayHeight}px` } : undefined}
            disabled={disabled}
            {...wrapTypography}
            onChange={(e) => {
              this.setState({ changes: getChangeValue(type, e.target.value) });
            }}
            onFocus={(e) => {
              if (!disabled && onFocus) {
                onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
              }
            }}
            onBlur={() => {
              const nextChanges = this.state.changes;
              if (focused) this.setState({ focused: false });
              if (!disabled && value !== nextChanges) onChange(fieldId, fieldIdentifier, fieldPositionId, nextChanges);
              if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
            }}
          />
        );
      } else {
        fieldNode = (
          <StyledInputGroupDisplay
            data-testid={`${fieldPositionId}-text-field`}
            data-ig-wrap-display="true"
            ref={(e) => { if (e) this.wrapDisplayHeight = e.offsetHeight; }}
            disabled={disabled}
            {...wrapTypography}
            onClick={() => {
              if (!disabled) {
                this.setState({ focused: true });
              }
            }}
          >
            {displayValue != null ? String(displayValue) : ''}
          </StyledInputGroupDisplay>
        );
      }

    } else {

      fieldNode = (
        <FieldInput
          data-testid={`${fieldPositionId}-text-field`}
          tabIndex={tabIndex}
          value={displayValue}
          type={getFieldInputType(disabled, type)}
          placeholder={placeholder}
          {...{
            disabled,
            focused,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
            primaryColor,
            fieldId,
            fieldMode,
            pageLayoutMode,
            fieldPresentationMode,
          }}
          onChange={(e) => {
            this.setState({ changes: getChangeValue(type, e.target.value) });
          }}
          onFocus={(e) => {

            if (!focused) this.setState({ focused: true });

            if (!disabled && onFocus && !focused) {
              onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: () => { e.target.blur(); } });
            }

          }}
          onBlur={(e) => {

            if (focused) this.setState({ focused: false });

            if (!disabled && value !== changes) onChange(fieldId, fieldIdentifier, fieldPositionId, changes);

            if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

          }}
        />
      );

    }

    return (
      <StyledWrapper {...{ margin }} style={wrapperStyles || {}}>
        <StyledInternalWrapper>
          <StyledFieldHeader>
            <FieldTitle
              {...{
                title,
                titleDisplay,
                titleFontSize,
                titleFontColor,
                titleTextAlign,
                titleFontStyle,
                titleFontWeight,
                titleTextTransform,
                titleTextDecoration,
              }}
            />
            <FieldDecorators
              decorators={decorators}
              fieldMode={fieldMode}
              fieldId={fieldId}
              fieldIdentifier={fieldIdentifier}
              fieldPositionId={fieldPositionId}
              onDecoratorClick={onDecoratorClick}
            />
          </StyledFieldHeader>
          {getTooltip()}
          {fieldNode}
        </StyledInternalWrapper>
      </StyledWrapper>
    );
  }
}

export const getFieldInputType = (disabled, type) => {
  return !disabled ? type : 'text';
};

export const getDisplayValue = (type, value) => {

  /**
   * <----------------------------NOTICE------------------------->
   * Text field component and functions inside this file are used in
   * many places.
   *
   * 0 is technically considered as a falsy value (!value) but, we have
   * value!==0 here so we could display 0 as the default value for xMin
   * and ymin in the chartFieldForm.
   *
   */
  if (!value && value !== 0) {
    return '';
  }
  else if (type === 'number' && !Number.isNaN(value)) {
    return value;
  }
  else if (value) {
    return value;
  }

};

export const getChangeValue = (type, value) => {

  let nextValue = value;

  if (type === 'number' && typeof nextValue === 'string' && nextValue !== '') {
    /**
     * This was to prevent the number from being saved as a string on the db
     */
    nextValue = nextValue.indexOf('.') === -1 ? parseInt(nextValue, 10) : parseFloat(nextValue, 10);

  }

  return nextValue;

};

export default TextField;
